import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { toast } from '@gitbook/spine-react';

import { apiClient, apiQuery, shouldSkipPublishDialog, useTrial } from '../../state';
import { useUpgradeDialogContext } from '../UpgradeDialog';
import { PublishSiteDialog } from './PublishSiteDialog';

export interface UseSitePublishProps {
    organization: api.Organization;
    // For executing extra work, like analytics
    onPress?: () => void;
    /** Called once the site is published, from the dialog or straight away. */
    onPublished?: (site: api.Site) => void;
}

export interface UseSitePublishReturn {
    dialogRootProps: {
        onOpenChange: (isOpen: boolean) => void;
        isOpen?: boolean;
    };
    publishDialogElement: React.ReactElement | null;
    publishSite: (site: api.Site) => Promise<void>;
}

export function useSitePublish(props: UseSitePublishProps): UseSitePublishReturn {
    const { organization, onPress, onPublished } = props;

    const [currentSite, setCurrentSite] = React.useState<api.Site | null>(null);
    const queryClient = useQueryClient();
    const { openUpgradeDialog } = useUpgradeDialogContext();
    const trial = useTrial({ organization });
    const isTrialingOrg = trial.status === api.BillingTrialStatus.Active;

    const handlePublishSuccess = (site: api.Site | null) => {
        if (site) {
            setCurrentSite(site);
            onPublished?.(site);
        }
    };

    const handleDialogClose = (isOpen: boolean) => {
        if (!isOpen) {
            setCurrentSite(null);
        }
    };

    const publishSite = async (site: api.Site) => {
        onPress?.();
        const isFrozen = site.type === api.SiteType.Basic && site.appliedType !== site.type;
        if (shouldSkipPublishDialog(site, organization, isTrialingOrg)) {
            const checkout = await queryClient.fetchQuery(
                apiQuery('previewSitePublishCheckout', {
                    organizationId: organization.id,
                    plan: site.type,
                })
            );

            if (
                checkout.type === 'go-ahead' ||
                checkout.type === 'trial-available' ||
                checkout.type === 'extended-trial-available'
            ) {
                const published =
                    (await toast.catch(async () => {
                        const { data: publishResult } = await apiClient.sites.publishSite(
                            organization.id,
                            site.id
                        );
                        return publishResult as api.Site;
                    }, 'Error while publishing site')) ?? null;

                if (published) {
                    setCurrentSite(published);
                    onPublished?.(published);
                }
                return;
            }

            if (checkout.type === 'checkout') {
                openUpgradeDialog({
                    organization,
                    targetProduct: api.BillingProduct.Pro2024,
                    reason: 'checkout_site',
                    initialState: {
                        publishedSites: new Map([[site.id, true]]),
                    },
                    analyticsContext: {
                        siteId: site.id,
                        siteType: site.type,
                        siteAppliedType: site.appliedType,
                        isFrozen,
                    },
                });
                return;
            }

            throw new Error('unknown checkout type');
        }

        setCurrentSite(site);
    };

    return {
        dialogRootProps: { onOpenChange: handleDialogClose, isOpen: currentSite !== null },
        publishDialogElement: (
            <PublishSiteDialog
                organization={organization}
                site={currentSite}
                onPublishSuccess={handlePublishSuccess}
            />
        ),
        publishSite,
    };
}
